import { useCallback, useEffect, useState } from 'preact/hooks';
import { t } from '../i18n';
import { store } from '../store';
import { navigateToView } from '../navigation';
import { openContextualLogin } from '../returnTo';
import { getConfig } from '../config';
import { addCoupon, getReferralProgram, type ReferralProgram } from '../api';
import { shareReferralLink } from '../referralShare';

function buildReferralLink(customerUid: string): string {
  if (!customerUid || typeof window === 'undefined') return '';
  const base = window.location.origin.replace(/\/+$/, '');
  return `${base}/?puid=${encodeURIComponent(customerUid)}`;
}

function ReferralFieldsSkeleton() {
  return (
    <div class="row g-3 bonus-account-card__copy-grid" aria-hidden="true">
      <div class="col-12 col-md-6">
        <div class="bonus-account-card__copy-card placeholder-glow">
          <span class="placeholder col-5 mb-2 d-block" style={{ height: '1rem' }} />
          <span class="placeholder col-12 d-block" style={{ height: '2.5rem' }} />
        </div>
      </div>
      <div class="col-12 col-md-6">
        <div class="bonus-account-card__copy-card placeholder-glow">
          <span class="placeholder col-5 mb-2 d-block" style={{ height: '1rem' }} />
          <span class="placeholder col-12 d-block" style={{ height: '2.5rem' }} />
        </div>
      </div>
    </div>
  );
}

/** Referral Program intro: text, referral link/code (copy), buttons to Transactions and Referral Tree */
export function BonusAccountPage() {
  const [, bump] = useState(0);
  const [program, setProgram] = useState<ReferralProgram | null>(null);
  const [applyingCoupon, setApplyingCoupon] = useState('');
  const config = getConfig();
  useEffect(() => store.subscribe(() => bump((n) => n + 1)), []);

  const auth = store.getAuth();
  const loading = store.getLoading();
  const customerUid = auth?.customer_uid ?? '';
  const showReferralTools = !loading && !!auth;

  const referralLink = program?.enabled ? program.referral_link : buildReferralLink(customerUid);
  const referralCode = program?.enabled ? program.referral_code : customerUid;

  useEffect(() => {
    if (!auth) {
      setProgram(null);
      return;
    }
    getReferralProgram(config).then((res) => {
      if (res.status === 200 && res.result) setProgram(res.result);
    }).catch(() => {});
  }, [auth?.login]);

  const applyProgramCoupon = useCallback(async (coupon: string, rewardPercent: number) => {
    setApplyingCoupon(coupon);
    try {
      const res = await addCoupon(config, coupon);
      const appliedCoupon = String((res.result as Record<string, unknown> | undefined)?.coupon ?? '').trim().toUpperCase();
      if (res.status === 200 && appliedCoupon === coupon.trim().toUpperCase()) {
        store.addToast(t('referralCoupon.applied', { reward: rewardPercent }), 'success');
        navigateToView('cart');
      } else {
        store.addToast(t('referralCoupon.applyFailed'), 'error');
      }
    } finally {
      setApplyingCoupon('');
    }
  }, []);

  const copyToClipboard = useCallback(async (value: string) => {
    if (!value) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed';
        ta.style.left = '-999999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        ta.remove();
      }
      store.addToast(t('bonus.copied'), 'success');
    } catch {
      store.addToast(t('bonus.copyFailed'), 'error');
    }
  }, []);

  const shareLink = useCallback(async (link: string) => {
    const result = await shareReferralLink(link);
    if (result === 'copied') store.addToast(t('referralShare.copied'), 'success');
    if (result === 'failed') store.addToast(t('referralShare.failed'), 'error');
  }, []);

  return (
    <div class="sw-tickets-shell bonus-account-shell">
      <div class="shop-widget-bonus-account sw-surface-card bonus-account-card">
        <h2 class="bonus-account-card__title text-center mb-4">{program?.enabled
          ? t('referralProgram.headline', { friend: program.offer.friend_percent, reward: program.offer.reward_percent })
          : t('bonus.headline')}</h2>
        <p class="mb-3">{program?.enabled
          ? t('referralProgram.intro', { reward: program.offer.reward_percent })
          : t('bonus.intro')}</p>

        {program?.enabled ? (
          <>
            <div class="bonus-account-card__section">
              <h4>{t('bonus.howTitle')}</h4>
              <p class="mb-0">{t('referralProgram.how', {
                friend: program.offer.friend_percent,
                reward: program.offer.reward_percent,
                months: program.offer.validity_months,
              })}</p>
            </div>
            <div class="bonus-account-card__section">
              <h4>{t('referralProgram.rulesTitle')}</h4>
              <ul class="list-group bonus-account-card__benefits mb-0">
                <li class="list-group-item">{t('referralProgram.ruleSeparate')}</li>
                <li class="list-group-item">{t('referralProgram.ruleOnePerOrder')}</li>
                <li class="list-group-item">{t('referralProgram.ruleNoLimit', { reward: program.offer.reward_percent })}</li>
              </ul>
            </div>
          </>
        ) : (
          <>
            <div class="bonus-account-card__section">
              <h4>{t('bonus.howTitle')}</h4>
              <p class="mb-2">{t('bonus.howDesc')}</p>
              <p class="mb-0">{t('bonus.subsequent')}</p>
            </div>
            <div class="bonus-account-card__section">
              <h4>{t('bonus.friendBenefits')}</h4>
              <p class="mb-0">{t('bonus.friendDiscount')}</p>
            </div>
            <div class="bonus-account-card__section">
              <h4>{t('bonus.whyGreat')}</h4>
              <ul class="list-group bonus-account-card__benefits mb-0">
                <li class="list-group-item">{t('bonus.benefit1')}</li>
                <li class="list-group-item">{t('bonus.benefit2')}</li>
                <li class="list-group-item">{t('bonus.benefit3')}</li>
              </ul>
            </div>
          </>
        )}

        <p class="bonus-account-card__share-note"><strong>{t('bonus.startSharing')}</strong></p>

        {loading && <ReferralFieldsSkeleton />}

        {!loading && !auth && config.referralProgramEnabled !== false && (
          <div class="d-flex flex-wrap align-items-center gap-2">
            <p class="text-muted mb-0">{t('referralShare.signIn')}</p>
            <button type="button" class="btn btn-outline-primary" onClick={() => openContextualLogin('bonus-account')}>{t('referralShare.signInButton')}</button>
          </div>
        )}

        {showReferralTools && (
          <>
            <div class="row g-3 bonus-account-card__copy-grid">
              <div class="col-12 col-md-6">
                <div class="bonus-account-card__copy-card">
                  <label class="form-label">{t('bonus.refLinkLabel')}</label>
                  <div class="input-group input-group-pill">
                    <input
                      class="form-control"
                      type="text"
                      readOnly
                      value={referralLink}
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                      onFocus={(e) => (e.target as HTMLInputElement).select()}
                      onKeyDown={(e) => e.key === 'Enter' && copyToClipboard(referralLink)}
                    />
                    <button type="button" class="btn btn-outline-secondary bonus-account-card__copy-btn" onClick={() => copyToClipboard(referralLink)}>
                      {t('bonus.copy')}
                    </button>
                  </div>
                  {program?.enabled && <button type="button" class="btn btn-outline-primary btn-sm mt-2" onClick={() => shareLink(program.referral_link)}>{t('referralShare.button')}</button>}
                </div>
              </div>
              <div class="col-12 col-md-6">
                <div class="bonus-account-card__copy-card">
                  <label class="form-label">{t('bonus.refCodeIntro')}</label>
                  <div class="input-group input-group-pill">
                    <input
                      class="form-control"
                      type="text"
                      readOnly
                      value={referralCode}
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                      onFocus={(e) => (e.target as HTMLInputElement).select()}
                      onKeyDown={(e) => e.key === 'Enter' && copyToClipboard(referralCode)}
                    />
                    <button type="button" class="btn btn-outline-secondary bonus-account-card__copy-btn" onClick={() => copyToClipboard(referralCode)}>
                      {t('bonus.copy')}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {program?.enabled && (
              <div class="bonus-account-card__section referral-program-coupon-list">
                <h4>{t('referralProgram.couponsTitle')}</h4>
                {program.coupons.length === 0 ? (
                  <p class="text-muted mb-0">{t('referralProgram.noCoupons')}</p>
                ) : program.coupons.map((coupon) => (
                  <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 border rounded p-3 mb-2" key={coupon.code}>
                    <div>
                      <strong>{coupon.percent}% off</strong>
                      <div><code>{coupon.code}</code></div>
                      <small class="text-muted">{t(`referralProgram.status.${coupon.status}`)} · {t('referralProgram.expires')} {new Date(coupon.expires_at).toLocaleDateString()}</small>
                    </div>
                    {coupon.status === 'active' && (
                      <button type="button" class="btn btn-primary" disabled={applyingCoupon === coupon.code} onClick={() => applyProgramCoupon(coupon.code, coupon.percent)}>
                        {applyingCoupon === coupon.code ? t('referralProgram.applying') : t('referralProgram.apply')}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div class="bonus-account-card__actions">
              {!program?.enabled && <button type="button" class="btn btn-primary w-100 bonus-account-card__action-btn" onClick={() => navigateToView('bonus-transactions')}>{t('bonus.transactions')}</button>}
              <button type="button" class="btn btn-success w-100 bonus-account-card__action-btn" onClick={() => navigateToView('referral-tree')}>
                {t('bonus.referralTree')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
